import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home } from 'lucide-react';
import RerunButton from '../components/RerunButton';
import { useModuleData } from '../hooks/useModuleData';

import Module01 from './modules/Module01';
import Module02 from './modules/Module02';
import Module03 from './modules/Module03';
import Module04 from './modules/Module04';
import Module05 from './modules/Module05';
import Module06 from './modules/Module06';
import Module07 from './modules/Module07';
import Module08 from './modules/Module08';

const MODULE_META = {
  '01': { name: 'Daily Motivation', Component: Module01 },
  '02': { name: 'Athlete Brief',    Component: Module02 },
  '03': { name: 'Fashion Feed',     Component: Module03 },
  '04': { name: 'Workout Plan',     Component: Module04 },
  '05': { name: 'Meal Plans',       Component: Module05 },
  '06': { name: 'Outfit Combos',    Component: Module06 },
  '07': { name: 'Deal Finder',      Component: Module07 },
  '08': { name: 'Income & Budget',  Component: Module08 },
};

export default function ModuleView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = MODULE_META[id];
  const { data, isLoading, isError } = useModuleData(id);

  if (!meta) return <div className="p-6 text-red-400">Unknown module.</div>;

  const { name, Component } = meta;

  return (
    <div className="max-w-lg mx-auto px-6 pb-10 safe-top safe-bottom">

      {/* Header */}
      <div className="pt-6 pb-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors active:opacity-60"
          >
            <ArrowLeft size={14} />
            <span className="text-[9px] tracking-[0.25em] uppercase">Back</span>
          </button>
          <RerunButton moduleId={id} />
        </div>

        <p className="text-[9px] tracking-[0.3em] uppercase mb-2" style={{ color: 'var(--gold)' }}>
          Module {id}
        </p>
        <h2 className="text-xl font-light tracking-[0.12em] uppercase">{name}</h2>
        <div className="mt-4 h-px bg-white/10" />
      </div>

      {/* Content */}
      {isLoading && <LoadingState />}
      {isError   && <ErrorState />}
      {data      && <Component data={data} />}
      {!isLoading && !isError && !data && <EmptyState name={name} />}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="border border-white/8 rounded h-20 animate-pulse bg-white/3" />
      ))}
    </div>
  );
}

function ErrorState() {
  return (
    <div className="border border-red-500/20 p-6 text-center">
      <p className="text-red-400 text-xs tracking-[0.2em] uppercase font-medium">Failed to load</p>
      <p className="text-gray-500 text-xs mt-2">Check that n8n is running and try re-running the module.</p>
    </div>
  );
}

function EmptyState({ name }) {
  return (
    <div className="border border-white/8 p-10 text-center">
      <p className="text-xs tracking-[0.2em] uppercase text-gray-400">{name} not yet generated</p>
      <p className="text-gray-600 text-xs mt-2">Tap Re-run to generate now.</p>
    </div>
  );
}
